import React, {
    createContext,
    useState,
    useContext,
    useCallback,
    useEffect,
    useRef,
    ReactNode,
    FC
} from 'react';
import { apiHandler } from '../api/responseHandler';
import config from '../../config';
import { useAuth } from '../../hooks/auth/useAuth';
import logger from '../logging/logger';

const componentName = 'CartContext';

// Define the shape of our context
interface CartContextType {
    isCartOpen: boolean;
    openCart: () => void;
    closeCart: () => void;
    cartCount: number;
    cartVersion: number;
    refreshCart: (forceUserId?: number) => Promise<number>;
    loading: boolean;
}

// Define props for the provider component
interface CartProviderProps {
    children: ReactNode;
}

// Create context with a default value
export const CartContext = createContext<CartContextType>({
    isCartOpen: false,
    openCart: () => { },
    closeCart: () => { },
    cartCount: 0,
    cartVersion: 0,
    refreshCart: (_forceUserId?: number) => Promise.resolve(0),
    loading: false
});

// Provider component
export const CartProvider: FC<CartProviderProps> = ({ children }) => {
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [cartCount, setCartCount] = useState(0);
    const [cartVersion, setCartVersion] = useState(0);
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();

    // Use ref to track userId and avoid dependency cascade from useAuth
    const userIdRef = useRef<number | undefined>(user?.userId);
    // Track current cartCount via ref so the catch block in fetchCartCount
    // (which has [] deps) can read the latest value instead of the stale initial 0
    const cartCountRef = useRef<number>(0);

    // Update ref when userId actually changes (by value, not reference)
    useEffect(() => {
        userIdRef.current = user?.userId;
    }, [user?.userId]);

    const openCart = useCallback(() => {
        setIsCartOpen(true);
    }, []);

    const closeCart = useCallback(() => {
        setIsCartOpen(false);
    }, []);

    // Fetch function that reads from ref - no dependencies on user object
    const fetchCartCount = useCallback(async (forceUserId?: number) => {
        const currentUserId = forceUserId ?? userIdRef.current;

        if (!currentUserId || currentUserId <= 0) {
            cartCountRef.current = 0;
            setCartCount(0);
            return;
        }

        logger.log('LogVerbose', componentName, 'Fetching cart count', {
            userId: currentUserId,
            forced: !!forceUserId
        });
        setLoading(true);

        try {
            const response = await apiHandler.fetch(
                `${config.API_URL}/api/order-group/${currentUserId}`,
                {
                    validateFn: (data: unknown) => data
                }
            );

            if (response.error) throw response.error;

            // Count total quantity of all items (Amazon-style)
            // API returns PascalCase fields (Quantity) from stored procedure
            const count = Array.isArray(response.data)
                ? response.data
                    .reduce((sum: number, item: Record<string, unknown>) =>
                        sum + (Number(item.Quantity) || 1), 0)
                : 0;

            cartCountRef.current = count;
            setCartCount(count);
            setCartVersion(v => v + 1);
            logger.log('LogVerbose', componentName, 'Cart count fetched', { count });

            return count;
        } catch (err) {
            // On error, retain previous cartCount instead of resetting to 0.
            // Resetting to 0 on transient failures (network blip, timeout) causes
            // the cart badge to vanish and can trigger the empty-cart redirect on
            // CheckoutPage, ejecting the user mid-checkout.
            logger.log('LogError', componentName, 'Error fetching cart count — retaining previous value', err);
            return cartCountRef.current;
        } finally {
            setLoading(false);
        }
    }, []); // No dependencies - reads from ref

    const refreshCart = useCallback((forceUserId?: number): Promise<number> => {
        return fetchCartCount(forceUserId);
    }, [fetchCartCount]);

    // Initialize cart count and set up polling - runs once on mount
    // Also refetch when userId changes (tracked separately)
    useEffect(() => {
        fetchCartCount();
        const intervalId = setInterval(fetchCartCount, 5 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, [fetchCartCount]);

    // Refetch when userId actually changes - pass explicit userId to avoid ref timing race
    useEffect(() => {
        fetchCartCount(user?.userId);
    }, [user?.userId, fetchCartCount]);

    const value: CartContextType = {
        isCartOpen,
        openCart,
        closeCart,
        cartCount,
        cartVersion,
        refreshCart,
        loading
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

// Hook for using the cart context
export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export default CartContext;